"use strict";
/**
 * Weekly Report V3 — Beat 3 What's in Motion
 *
 * Two card types power the "What's in Motion" carousels and the product
 * detail conclusion state. Each card is a *frozen snapshot* of the
 * underlying observation window (wr_routine_change) at the moment the
 * report was generated. Going back to a four-week-old report should show
 * what was true at that time, not the current state of the window.
 *
 *   - WindowConclusion — emitted when an observation window has just
 *     closed. Carries Stella's verdict (keep | fuzzy | step_back) plus an
 *     editorial read. Renders in the Conclusions carousel and as the
 *     in-place replacement for the Observation Window card on product
 *     detail.
 *
 *   - ActiveTry — emitted while an observation window is still open.
 *     Carries the routine-change kind, the window state at gen time, the
 *     window's start date + length, and a forward-looking read. The
 *     pre_window variant has no useful window math yet — frontend
 *     computes "Day N" from addedAt + reportDate and omits the bar.
 *
 * Both card types snapshot every field needed to render faithfully. The
 * wrRoutineChangeId FK is supplementary, used only for tap-through
 * navigation and (on the conclusion view) for live decision-state
 * lookup, since the Keep/Pause decision is made *after* the report is
 * generated and a 2-week-old conclusion card should reflect the current
 * decision state.
 *
 * Display strings (verdict labels, kind labels, "Observed N weeks",
 * chip text, color tokens) are NOT in the schema — the frontend resolves
 * them via i18n + getSuggestionColor() + small windowState->color maps
 * at render time. This keeps copy editable and i18n-ready while leaving
 * the JSONB frozen for historical fidelity.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.StableRoutineCountSchema = exports.ActiveTrySchema = exports.WindowConclusionSchema = exports.ConfounderItemSchema = exports.WindowConclusionReasoningSchema = exports.MotionTapTargetSchema = exports.refineAdvicePairing = exports.AdviceReasonEnum = exports.AdviceModeEnum = exports.ActiveTryWindowStateEnum = exports.SuggestionTypeSchema = exports.VerdictSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("../api/enums");
// ============================================================================
// SHARED ENUMS (re-exported from api/enums for caller convenience)
// ============================================================================
exports.VerdictSchema = zod_1.z.enum(enums_1.routineVerdictEnum);
exports.SuggestionTypeSchema = zod_1.z.enum(enums_1.suggestionTypeEnum);
// ============================================================================
// ACTIVE TRY — window state
// ============================================================================
/**
 * Where the product/treatment sits in its observation window:
 *
 *   pre_window  → added < 7 days ago, no window math yet (no progress bar)
 *   early       → < 25% through window
 *   emerging    → 25-75% through window
 *   closing     → > 75% through window
 *
 * Computed at gen time from (reportDate - addedAt) / observationWindowWeeks.
 * Used as a sort key on the backend and as the discriminator the frontend
 * keys off for color tokens and chip text format.
 */
exports.ActiveTryWindowStateEnum = zod_1.z.enum([
    'pre_window',
    'early',
    'emerging',
    'closing',
]);
// ============================================================================
// OBSERVE-ONLY (shared by ActiveTry + WindowConclusion)
// ============================================================================
/**
 * POLICY (2026-07-05 safety audit): 'observe_only' windows are measured but
 * never graded — Stella withholds advice and adjudication. Shared by the open
 * window (ActiveTry) and the closed window (WindowConclusion) so the hands-off
 * framing is shown UP FRONT while the window is open, not only at close.
 */
exports.AdviceModeEnum = zod_1.z.enum(['advice', 'observe_only']);
/**
 * Why advice is withheld. Null when adviceMode is 'advice'.
 * - 'prescription': Rx product — Stella measures but defers to the prescriber.
 * - 'pregnancy': contraindicated active / clinic treatment while pregnant.
 * - 'sunscreen': SPF is foundational; deterministic-only, never graded.
 */
exports.AdviceReasonEnum = zod_1.z.enum([
    'prescription',
    'pregnancy',
    'sunscreen',
]);
/**
 * Machine-check the adviceMode/adviceReason pairing so a frozen JSONB row can
 * never claim advice was withheld without saying why (or carry a reason on the
 * normal advice path). Defaults apply first, so historical rows missing both
 * fields parse as advice/null.
 */
const refineAdvicePairing = (c, ctx) => {
    const paired = (c.adviceMode === 'advice') === (c.adviceReason === null);
    if (!paired) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['adviceReason'],
            message: "adviceReason must be null exactly when adviceMode is 'advice'",
        });
    }
};
exports.refineAdvicePairing = refineAdvicePairing;
// ============================================================================
// TAP TARGET (shared)
// ============================================================================
/** Where a card taps through to. */
exports.MotionTapTargetSchema = zod_1.z.object({
    screen: zod_1.z.enum(['product_detail', 'treatment_detail']),
    id: zod_1.z.number(),
});
// ============================================================================
// WINDOW CONCLUSION CARD
// ============================================================================
/**
 * Conclusions carousel card. One per observation window that closed in the
 * report period.
 *
 * Snapshots are frozen in JSONB. wrRoutineChangeId is the FK back to the
 * source row, used for tap-through navigation (id resolves to the
 * product/treatment detail screen, which fetches its own live data).
 *
 * Exactly one of productId / treatmentId is non-null (matches the source
 * wr_routine_change row).
 */
/**
 * Structured reasoning the LLM emits alongside the verdict. Persisted in
 * the JSONB so QA can audit verdicts post-hoc and validation set work
 * can compare what the LLM cited vs what the underlying evidence
 * showed. The user-facing card only renders `read` + verdict; this
 * object is for backend telemetry and admin tooling.
 */
exports.WindowConclusionReasoningSchema = zod_1.z.object({
    /** What should have happened given product + ingredient mechanism. */
    expectedOutcome: zod_1.z.string().min(1).max(400),
    /** What actually happened across the window, citing specific metrics. */
    observedOutcome: zod_1.z.string().min(1).max(400),
    /**
     * Explicit recognition flag for known patterns. The verdict prompt
     * asks the LLM to set this when a purge phase recovery or ceiling
     * effect is the load-bearing piece of the reasoning, so we can
     * validate later that those cases were called out.
     */
    purgeOrCeilingNoted: zod_1.z
        .enum(['purge_recognized', 'ceiling_recognized'])
        .nullable(),
    /**
     * Each routine-internal or lifestyle confounder the LLM weighed and
     * how it factored into the verdict. Capped at 8 to prevent runaway
     * verbosity.
     */
    confoundersConsidered: zod_1.z.array(zod_1.z.string().max(200)).max(8),
    /**
     * What evidence would point to a different verdict if we trusted it
     * more. "none" is a valid value when the verdict is clean.
     */
    counterEvidence: zod_1.z.string().max(400),
});
/**
 * One row of the "What else was in motion" block on the product-detail
 * conclusion view. Backend emits structured flags only — frontend
 * renders the body string via i18n with the directional effect baked
 * into the copy. See `docs/superpowers/specs/2026-04-30-confounder-pipeline-design.md`.
 */
exports.ConfounderItemSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('lifestyle_signal'),
        /**
         * Schema-canonical signal name. Activity is intentionally not in
         * this set — its directional effect on skin is genuinely ambiguous,
         * so we don't surface it as a user-facing confounder.
         */
        signal: zod_1.z.enum(['sleep', 'stress_recovery', 'skin_temp']),
        direction: zod_1.z.enum(['consistently_better', 'consistently_worse', 'mixed']),
        /** Count of weeks meeting the per-bucket significance threshold. */
        weeksAffected: zod_1.z.number().int().min(2),
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('routine_overlap'),
        productName: zod_1.z.string().min(1),
        interactionFlag: zod_1.z.enum([
            'antagonistic',
            'over_exfoliation',
            'synergistic',
        ]),
        /** 1-indexed week within the observation window. */
        addedAtWeek: zod_1.z.number().int().min(1),
    }),
]);
exports.WindowConclusionSchema = zod_1.z
    .object({
    /** Source-of-truth window row. Used for nav + live decision lookup only. */
    wrRoutineChangeId: zod_1.z.number(),
    productId: zod_1.z.number().nullable(),
    treatmentId: zod_1.z.number().nullable(),
    /** Snapshot. Stella's verdict at the moment the window closed. */
    verdict: exports.VerdictSchema,
    /**
     * 0-1. How confident the LLM is in the verdict. Used for telemetry
     * (low-confidence flagging) and verdict-vs-decision divergence
     * analysis post-ship. Inclusive bounds.
     */
    confidence: zod_1.z.number().min(0).max(1),
    /** Snapshot. Frozen even if the product is renamed later. */
    productName: zod_1.z.string(),
    /** Snapshot — ISO. Used by frontend i18n to render dates + duration. */
    windowStartedAt: zod_1.z.string(),
    windowClosedAt: zod_1.z.string(),
    /** Snapshot — used by frontend i18n: "Observed N weeks". */
    windowWeeks: zod_1.z.number(),
    /**
     * LLM-generated narrative. Per-report copy. Passes banned-phrase regex.
     * Null when the LLM call failed or its output tripped compliance —
     * frontend renders the i18n fallback string in that case. Backend
     * never ships English fallback prose.
     */
    read: zod_1.z.string().max(200).nullable(),
    /**
     * Structured reasoning. Persisted for QA / validation set work; not
     * shown to users.
     */
    reasoning: exports.WindowConclusionReasoningSchema,
    /**
     * Optional "Stella is watching X" line surfaced under a keep_using
     * verdict when a metric trended adversely but didn't justify
     * step_back. Lets the LLM nuance a positive verdict without
     * downgrading it. Null when no concern to flag.
     */
    concernNote: zod_1.z.string().min(1).max(120).nullable(),
    /**
     * Structured "what else was in motion" rows, surfaced on the
     * product-detail conclusion view. Defaults to `[]` so historical
     * rows that predate this field still parse cleanly. Cap at 3 is
     * enforced by the backend helper; the schema only enforces the
     * shape of each item.
     */
    confounders: zod_1.z.array(exports.ConfounderItemSchema).default([]),
    /**
     * Pregnancy callout flag. Set deterministically by the service when the
     * user is female AND the active being evaluated is on the
     * MECHANISM_MATRIX pregnancy_contraindicated list. The LLM never sees
     * or generates this. Frontend reads the flag and renders the i18n copy
     * below the read; verdict is unaffected. Backend MUST NOT carry the
     * user-facing string — copy is a frontend concern.
     */
    showPregnancyCallout: zod_1.z.boolean(),
    /**
     * POLICY (2026-07-05 safety audit): 'observe_only' conclusions state
     * what Stella observed but deliberately withhold advice — the LLM read
     * is null, the frontend renders a neutral i18n template keyed by
     * adviceReason, and Keep / Remove actions are hidden. Live today for
     * pregnancy-contraindicated products and clinic treatments while the
     * user is pregnant; the 'prescription' reason is emitted once the Rx
     * hands-off slice lands (until then no code path produces it).
     * Defaults keep historical rows parsing as the normal advice path.
     */
    adviceMode: exports.AdviceModeEnum.default('advice'),
    /** Why advice is withheld. Null when adviceMode is 'advice'. See AdviceReasonEnum. */
    adviceReason: exports.AdviceReasonEnum.nullable().default(null),
    /**
     * Early-discontinuation flag. True when the conclusion is for a window
     * the user removed from their routine BEFORE it closed naturally. The
     * verdict is forced to `not_sure` for these cases — the user already
     * stepped back, so Stella's job is informational closure rather than a
     * recommendation. Frontend renders without Keep / Pause decision
     * buttons since the decision is already made.
     *
     * Orchestrator only emits an early-conclusion card when the window was
     * observed for at least 2 weeks; sub-2-week removals are silently
     * dropped.
     */
    discontinuedEarly: zod_1.z.boolean(),
    tapTarget: exports.MotionTapTargetSchema,
})
    .superRefine(exports.refineAdvicePairing);
// ============================================================================
// ACTIVE TRY CARD
// ============================================================================
/**
 * In Progress carousel card. One per active observation window.
 *
 * `kind` is the suggestion type that caused the routine change. It's null
 * when the source row is a treatment (treatmentId !== null) — treatments
 * don't go through the suggestion pipeline, so the frontend renders the
 * deterministic "Treatment started" label keyed off treatmentId presence.
 *
 * windowState is computed at gen time and snapshotted; the frontend
 * derives chip text and progress bar fill from (reportDate - addedAt) /
 * windowWeeks. Color tokens map deterministically from windowState.
 *
 * Exactly one of productId / treatmentId is non-null.
 */
exports.ActiveTrySchema = zod_1.z.object({
    /** Source-of-truth window row. Used for tap-through navigation only. */
    wrRoutineChangeId: zod_1.z.number(),
    productId: zod_1.z.number().nullable(),
    treatmentId: zod_1.z.number().nullable(),
    /** Suggestion-type kind for product tries. Null for treatment tries. */
    kind: exports.SuggestionTypeSchema.nullable(),
    /** Snapshot — frontend uses this for chip text format + color tokens. */
    windowState: exports.ActiveTryWindowStateEnum,
    /** Snapshot — ISO. Frontend uses to compute weekCurrent + daysSinceAdd. */
    addedAt: zod_1.z.string(),
    /** Snapshot — used to compute progress at render time. */
    windowWeeks: zod_1.z.number(),
    /** Snapshot — frozen even if the product is renamed later. */
    productName: zod_1.z.string(),
    /**
     * LLM-generated narrative. Per-report copy. Passes banned-phrase regex.
     * Null when the LLM call failed or its output tripped compliance —
     * frontend renders the i18n fallback keyed off `windowState`. Backend
     * never ships English fallback prose.
     */
    read: zod_1.z.string().max(200).nullable(),
    /**
     * Observe-only framing shown WHILE the window is open (2026-07-05 safety
     * audit, Slice 3). 'observe_only' → Stella measures but won't grade this
     * window; the frontend renders the reason call-out up front so the user
     * knows from day one. Defaults keep historical rows on the advice path.
     */
    adviceMode: exports.AdviceModeEnum.default('advice'),
    adviceReason: exports.AdviceReasonEnum.nullable().default(null),
    tapTarget: exports.MotionTapTargetSchema,
}).superRefine(exports.refineAdvicePairing);
// ============================================================================
// STABLE COUNT (label under the "See your full routine" link)
// ============================================================================
/** Counts of products and treatments not surfaced as conclusions/active tries. */
exports.StableRoutineCountSchema = zod_1.z.object({
    products: zod_1.z.number(),
    treatments: zod_1.z.number(),
});
//# sourceMappingURL=motion.js.map